import { AccountClass } from "@common/constants/domains/accounting/accounts";

/**
 * Normalized elimination source-fact format (M2-A2 folded into M2-B1).
 *
 * One shape spans every elimination input: intercompany due-to/from balances,
 * intercompany P&L legs, opening balances, and ownership/equity mappings. The
 * exact-match engine consumes `EliminationSourceFact[]` and never persists
 * anything — eliminations are recomputed on read (AC 2.3 / 2.4).
 */
export enum EliminationFactType {
    icBalanceSheet = "ic_balance_sheet",
    icProfitLoss = "ic_profit_loss",
    openingBalance = "opening_balance",
    ownershipEquity = "ownership_equity",
}

export interface EliminationProvenance {
    journalEntryIds: string[];
    openingBalanceIds?: string[];
}

/**
 * A single normalized leg booked by one entity toward an in-group counterparty.
 * `signedAmount` follows the journal-entry debit-positive convention
 * (`debitAmount - creditAmount`) summed over the period.
 */
export interface EliminationSourceFact {
    factType: EliminationFactType;
    entityId: number;
    otherEntityId: number;
    accountId: string;
    accountClass: AccountClass;
    signedAmount: number;
    provenance: EliminationProvenance;
    /**
     * Present for journal-entry-sourced facts, enabling JE-grain matching.
     * Absent for pair-aggregate inputs (opening balances, ownership/equity).
     */
    journalEntryId?: string;
    /** ISO date of the source journal entry; pairing tiebreak. */
    date?: string;
    /**
     * `<ledgerId>:<referenceNumber>` of the source journal entry, linking a
     * P&L contra leg to the IC balance-sheet leg of the same entry.
     */
    entryKey?: string;
    /**
     * Stored cross-ledger pairing key for transaction-sourced facts:
     * `txn:<transactionId>` (the inter-entity fan-out books both ledgers with
     * one transaction id) or `transfer:<lowTxnId>:<highTxnId>` (transactions
     * linked by pairedTransferId). Absent for manual journal entries — those
     * are paired by the amount-mirror heuristic instead.
     */
    linkKey?: string;
    /**
     * True on a matched-transfer fact whose paired leg's transaction date
     * falls outside the report period — the other side is booked, just not
     * visible in this window.
     */
    inTransit?: boolean;
    /** Source transaction of the journal-entry line, when transaction-sourced. */
    transactionId?: number;
    /**
     * True on a P&L leg whose journal entry transited multiple distinct
     * intercompany counterparties: the leg cannot be attributed to a single
     * pair, so the matcher routes it to an `ambiguous_multi_counterparty_je`
     * exception instead of eliminating it against an arbitrary counterparty.
     */
    ambiguousPair?: boolean;
}

export interface EliminationPair {
    entityId: number;
    otherEntityId: number;
}

/**
 * A report-layer eliminating adjustment for one account. `amount` is the
 * debit-positive value that nets the account's matched balance to zero, i.e.
 * the negation of the source fact's `signedAmount`.
 */
export interface EliminationEntry {
    pair: EliminationPair;
    matchGroupKey: string;
    factType: EliminationFactType;
    accountId: string;
    accountClass: AccountClass;
    amount: number;
    provenance: EliminationProvenance;
    /** ISO date of the source journal entry; absent for aggregate inputs. */
    date?: string;
}

export enum EliminationExceptionReason {
    /** Pair transited intercompany accounts but the two sides did not match. */
    unmatchedIc = "unmatched_ic",
    /**
     * Informational: a matched transfer whose paired leg's date falls outside
     * the report period — correct bookkeeping, visible next period.
     */
    inTransitTransfer = "in_transit_transfer",
    /**
     * A single journal entry transited two or more distinct intercompany
     * counterparties, so its P&L legs cannot be attributed to one pair. The
     * P&L is surfaced here instead of being silently attributed to an
     * arbitrary counterparty — the entry should be split per counterparty.
     */
    ambiguousMultiCounterpartyJe = "ambiguous_multi_counterparty_je",
}

export interface EliminationExceptionSide {
    entityId: number;
    otherEntityId: number;
    accountId: string;
    signedAmount: number;
    provenance: EliminationProvenance;
}

export interface EliminationException {
    reason: EliminationExceptionReason;
    factType: EliminationFactType;
    pair: EliminationPair;
    sides: EliminationExceptionSide[];
    /** Residual that did not eliminate (debit-positive). */
    netAmount: number;
    /**
     * Source transactions behind the excepted facts (sorted ascending), when
     * transaction-sourced — the fixable objects to deep-link to. Absent for
     * purely manual exceptions.
     */
    transactionIds?: number[];
}

/**
 * A one-sided P&L leg on an otherwise matched JE pair (e.g. an expense funded
 * by a sibling entity's account): real third-party activity, not eliminated
 * and not excepted, carried on the result so report-layer tie-outs can
 * reconcile totals.
 */
export interface EliminationPassThrough {
    pair: EliminationPair;
    factType: EliminationFactType;
    accountId: string;
    accountClass: AccountClass;
    signedAmount: number;
    provenance: EliminationProvenance;
    journalEntryId?: string;
}

export interface EliminationResult {
    eliminations: EliminationEntry[];
    exceptions: EliminationException[];
    passThroughs?: EliminationPassThrough[];
}
